import { type Session } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, use, useEffect, useState, type PropsWithChildren } from 'react'
import { usePostHog } from '~/lib/analytics/posthog'
import { isUserAnonymous } from '~/lib/auth/anonymous'
import { supabase } from '~/lib/supabase'
import { trpc } from '~/lib/trpc'

const AuthContext = createContext<{
  session?: Session | null
  isLoading: boolean
  isAnonymous: boolean
}>({
  session: null,
  isLoading: false,
  isAnonymous: false,
})

export function useSession() {
  const value = use(AuthContext)

  if (!value) {
    throw new Error('useSession must be wrapped in a <SessionProvider />')
  }

  return value
}

export function useUserId() {
  const { session } = useSession()
  return session?.user?.id ?? ''
}

export function SessionProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const queryClient = useQueryClient()
  const posthog = usePostHog()

  useEffect(() => {
    const onSessionChange = async (session: Session | null) => {
      setSession(session)

      await queryClient.invalidateQueries({
        queryKey: trpc.users.isAdmin.queryKey(),
      })

      if (session) {
        posthog.identify(session.user.id, {
          email: session.user.email ?? null,
          name: session.user.user_metadata.name ?? '(Anonymous)',
          phoneNumber: session.user.phone ?? null,
        })
      } else {
        posthog.reset()
      }

      setIsLoading(false)
    }

    void Promise.race([
      supabase.auth.getSession().then(({ data: { session } }) => onSessionChange(session)),
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ]).then(() => setIsLoading(false))

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void onSessionChange(session)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [posthog, queryClient])

  return (
    <AuthContext
      value={{
        session,
        isLoading,
        isAnonymous: isUserAnonymous(session?.user),
      }}>
      {children}
    </AuthContext>
  )
}
